/** Small DOM helpers, so the app code reads as intent rather than plumbing. */

/** Look up a required element, failing loudly rather than at first use. */
export function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`Missing element #${id}`);
  return node as T;
}

export function all<T extends HTMLElement>(selector: string, root: ParentNode = document): T[] {
  return Array.from(root.querySelectorAll<T>(selector));
}

/**
 * The three event maps the app listens on, merged. Elements, the document and
 * the window each declare their own, and a listener helper that only knows
 * about one of them rejects `visibilitychange` and `pagehide` — both of which
 * matter here, because a backgrounded phone silently ends the camera track.
 */
type AnyEventMap = HTMLElementEventMap & DocumentEventMap & WindowEventMap;

/** Add a listener and hand back the function that removes it. */
export function on<K extends keyof AnyEventMap>(
  target: EventTarget,
  type: K,
  handler: (event: AnyEventMap[K]) => void,
  options?: AddEventListenerOptions,
): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}

/** Reflect a boolean into both the class the CSS reads and the ARIA state. */
export function setPressed(button: HTMLElement, pressed: boolean): void {
  button.classList.toggle('is-on', pressed);
  button.setAttribute('aria-pressed', String(pressed));
}

export function setHidden(node: HTMLElement, hidden: boolean): void {
  node.toggleAttribute('hidden', hidden);
}

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

/** Yield a frame, so a class change gets a chance to paint before the next one. */
export const nextFrame = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => resolve()));

/** True when the person has asked the system for less movement. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Human-readable byte count, for the storage line in settings. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** "3 minutes ago", falling back to a date once that stops being useful. */
export function formatWhen(timestamp: number, now = Date.now()): string {
  const seconds = Math.round((now - timestamp) / 1000);
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
